import { Button, Card, Divider, Form, Input, message, Modal, Select, Switch, Typography } from 'antd';
import React, { useState } from 'react';
import { ApiHandler } from '../services/apiHandler';
import { getTechnology } from '../services/helper';

interface CreateRfSimulationProps {
  showRfAnalysis: boolean;
  setShowRfAnalysis: (value: boolean) => void;
  rfAnalysisForm: any;
  setRfAnalysisForm: (form: any) => void;
  showRfRadius: boolean;
  setShowRfRadius: (value: boolean) => void;
  runMultipleRFAnalysis: () => void;
  assets: any[];
  rfResults: any;
  rfParameters: any;
  rfImageUrl: string;
  rfBounds: any;
  rfLocation: any;
  goBack: () => void;
  isViewSimulation: boolean;
}

const CreateRfSimulation: React.FC<CreateRfSimulationProps> = (props) => {
  const [saveConfirmationVisible, setSaveConfirmationVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [simulationName, setSimulationName] = useState('');
  const saveRfSimulation = () => {
    setIsLoading(true);
    ApiHandler({
      apiName: 'saveRFSimulation',
      params: {},
      body: {
        simulation_name: simulationName,
        rf_results: props.rfResults,
        rf_parameters: props.rfParameters,
        rf_image_url: props.rfImageUrl,
        rf_bounds: props.rfBounds,
        technology: getTechnology(),
        transmitters: props.assets.map((asset: any) => asset.id),
        location: {
          latitude: props.rfLocation.lat,
          longitude: props.rfLocation.lng,
        },
        user_selections: props.rfAnalysisForm,
      },
    })
      .then((response: any) => {
        console.log(response, 'response');
        message.success('RF simulation saved successfully');
        setSaveConfirmationVisible(false);
        props.goBack();
      })
      .catch((error: any) => {
        console.log(error, 'error');
        message.error(error?.data?.message ?? error?.message ?? 'Failed to save RF simulation');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  return (
    <>
      <div className="position-absolute d-flex flex-column gap-2 overflow-auto" style={{ top: '16px', left: '16px', bottom: '16px', width: '400px' }}>
        <Card
          title={
            <div className="d-flex flex-row justify-content-between pt-2">
              <Typography.Title level={4}>RF Simulation</Typography.Title>
              <Button type="link" color="danger" style={{ color: 'red' }} onClick={() => props.goBack()}>
                Cancel
              </Button>
            </div>
          }
          id="rf-analysis-card">
          {!props.isViewSimulation && (
            <Button
              className="mb-2 w-100"
              type="primary"
              onClick={() => {
                if (!props.rfResults || !props.rfParameters || !props.rfImageUrl || !props.rfBounds || !props.rfLocation) {
                  message.error('Nothing to save.Please run the RF simulation first');
                  return;
                }
                setSaveConfirmationVisible(true);
              }}>
              Save Simulation
            </Button>
          )}
          {!props.isViewSimulation && (
            <Button type="default" className="mb-4 w-100" onClick={() => props.runMultipleRFAnalysis()}>
              Run RF Simulation
            </Button>
          )}
          {/* <div className="row-wise vertical-center justify-content-start gap-2 mb-3">
            <Typography.Text>RF Heatmap</Typography.Text>
            <Switch
              checked={props.showRfAnalysis}
              onChange={(value) => props.setShowRfAnalysis(value)}
              checkedChildren="Hide"
              unCheckedChildren="Show"
            />
          </div> */}
          <div className="d-flex flex-column gap-1 mt-2">
            <Form autoComplete="off" labelAlign="left" className="overflow-auto" disabled={props.isViewSimulation}>
              <Form.Item label="Simulation Radius" name="radius" className="my-0">
                <Input
                  type="number"
                  addonAfter="m"
                  size="small"
                  value={props.rfAnalysisForm.radius}
                  defaultValue={props.rfAnalysisForm.radius}
                  onChange={(e) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, radius: parseFloat(e.target.value) })}
                />
              </Form.Item>
              <Form.Item label="Node Search Radius" name="internal_radius" className="my-0">
                <Input
                  type="number"
                  addonAfter="m"
                  size="small"
                  value={props.rfAnalysisForm.internal_radius}
                  defaultValue={props.rfAnalysisForm.internal_radius}
                  onChange={(e) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, internal_radius: parseFloat(e.target.value) })}
                />
              </Form.Item>
            </Form>

            <Switch
              className="align-self-start mt-2"
              checked={props.showRfRadius}
              onChange={(value: boolean) => props.setShowRfRadius(value)}
              checkedChildren="Hide Radius"
              unCheckedChildren="Show Radius"
            />

            <Divider />

            <Form autoComplete="off" labelAlign="left" disabled={props.isViewSimulation}>
              <Form.Item label="Simulation Provider" name="simulation_provider" className="my-0">
                <Select
                  value={props.rfAnalysisForm.simulation_provider}
                  defaultValue={props.rfAnalysisForm.simulation_provider}
                  onChange={(value: any) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, simulation_provider: value })}
                  placeholder="Select"
                  size="small"
                  className="w-100"
                  options={[{ label: 'CloudRF', value: 'cloudrf' }]}
                />
              </Form.Item>
            </Form>

            <Divider />

            <Typography.Text strong>Receiver</Typography.Text>

            <Form labelCol={{ span: 10 }} wrapperCol={{ span: 24 }} autoComplete="off" labelAlign="left" disabled={props.isViewSimulation}>
              <Form.Item label="Altitude" name="receiver_height" className="my-0">
                <Input
                  type="number"
                  addonAfter="m"
                  size="small"
                  value={props.rfAnalysisForm.receiver_height}
                  defaultValue={props.rfAnalysisForm.receiver_height}
                  onChange={(e) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, receiver_height: parseFloat(e.target.value) })}
                />
              </Form.Item>
              <Form.Item label="Antenna Gain" name="receiver_gain" className="my-0">
                <Input
                  type="number"
                  addonAfter="dBi"
                  size="small"
                  value={props.rfAnalysisForm.receiver_gain}
                  defaultValue={props.rfAnalysisForm.receiver_gain}
                  onChange={(e) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, receiver_gain: parseFloat(e.target.value) })}
                />
              </Form.Item>
              <Form.Item label="Antenna Sensitivity" name="receiver_sensitivity" className="my-0">
                <Input
                  type="number"
                  addonAfter="dBm"
                  size="small"
                  value={props.rfAnalysisForm.receiver_sensitivity}
                  defaultValue={props.rfAnalysisForm.receiver_sensitivity}
                  onChange={(e) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, receiver_sensitivity: parseFloat(e.target.value) })}
                />
              </Form.Item>
            </Form>

            <Divider />

            <Typography.Text strong>Propagation</Typography.Text>
            <Form labelCol={{ span: 11 }} wrapperCol={{ span: 24 }} autoComplete="off" labelAlign="left" disabled={props.isViewSimulation}>
              <Form.Item label="Model" name="pm" className="my-0">
                <Select
                  defaultValue={props.rfAnalysisForm.pm}
                  value={props.rfAnalysisForm.pm}
                  onChange={(value: any) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, pm: value })}
                  placeholder="Select"
                  size="small"
                  className="w-100"
                  options={[
                    { label: 'Irregular Terrain Model (ITM) (Longley-Rice)', value: 1 },
                    { label: 'Line of Sight (LOS)', value: 2 },
                    { label: 'Okumura-Hata', value: 3 },
                    { label: 'ITU-R P.1546', value: 4 },
                    { label: 'SUI Microwave', value: 5 },
                    { label: 'COST231-Hata', value: 6 },
                    { label: 'ITU-R P.525 (Free space path loss)', value: 7 },
                    { label: 'RADAR', value: 8 },
                    { label: 'Ericsson 9999', value: 9 },
                    { label: 'General Purpose', value: 10 },
                    { label: 'Egli VHF/UHF', value: 11 },
                    { label: 'HF NVIS', value: 12 },
                  ]}
                />
              </Form.Item>
              <Form.Item label="Model Sub-Type" name="pe" className="my-0">
                <Select
                  value={props.rfAnalysisForm.pe}
                  defaultValue={props.rfAnalysisForm.pe}
                  onChange={(value: any) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, pe: value })}
                  placeholder="Select"
                  size="small"
                  className="w-100"
                  options={[
                    { label: 'Conservative (Urban)', value: 1 },
                    { label: 'Average (Suburban)', value: 2 },
                    { label: 'Optimistic (Rural)', value: 3 },
                  ]}
                />
              </Form.Item>
              <Form.Item label="Knife Edge Diffraction" name="ked" className="my-0">
                <Select
                  value={props.rfAnalysisForm.ked}
                  defaultValue={props.rfAnalysisForm.ked}
                  onChange={(value: any) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, ked: value })}
                  placeholder="Select"
                  size="small"
                  className="w-100"
                  options={[
                    { label: 'Diffraction disabled', value: 0 },
                    { label: 'Knife-edge diffraction', value: 1 },
                    { label: 'Bullington diffraction', value: 2 },
                    { label: 'Giovanelli diffraction', value: 3 },
                    { label: 'Deygout diffraction', value: 4 },
                    { label: 'Epstein-Peterson diffraction', value: 5 },
                  ]}
                />
              </Form.Item>
              <Form.Item label="Model Reliability" name="rel" className="my-0">
                <Select
                  value={props.rfAnalysisForm.rel}
                  defaultValue={props.rfAnalysisForm.rel}
                  onChange={(value: any) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, rel: value })}
                  placeholder="Select"
                  size="small"
                  className="w-100"
                  options={[
                    { label: '50%', value: 50 },
                    { label: '60%', value: 60 },
                    { label: '70%', value: 70 },
                    { label: '80%', value: 80 },
                    { label: '90%', value: 90 },
                    { label: '95%', value: 95 },
                  ]}
                />
              </Form.Item>
              <Form.Item label="RADAR Cross Section" name="rcs" className="my-0">
                <Input
                  type="number"
                  addonAfter="sq.m"
                  size="small"
                  min={0.0001}
                  max={10000}
                  value={props.rfAnalysisForm.rcs}
                  defaultValue={props.rfAnalysisForm.rcs}
                  onChange={(e) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, rcs: parseFloat(e.target.value) })}
                />
              </Form.Item>
            </Form>

            <Divider />

            <Typography.Text strong>Environment</Typography.Text>
            <Form labelCol={{ span: 9 }} wrapperCol={{ span: 24 }} autoComplete="off" labelAlign="left" disabled={props.isViewSimulation}>
              <Form.Item label="Elevation Model" name="elevation" className="my-0">
                <Select
                  value={props.rfAnalysisForm.elevation}
                  defaultValue={props.rfAnalysisForm.elevation}
                  onChange={(value: any) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, elevation: value })}
                  placeholder="Select"
                  size="small"
                  className="w-100"
                  options={[
                    { label: 'Digital Surface Model (DSM)', value: 1 },
                    { label: 'Digital Terrain Model (DTM)', value: 2 },
                  ]}
                />
              </Form.Item>
              <Form.Item label="Landcover" name="landcover" className="my-0">
                <Switch
                  size="small"
                  checked={props.rfAnalysisForm.landcover === 1}
                  onChange={(value: any) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, landcover: value ? 1 : 0 })}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </Form.Item>
              <Form.Item label="Buildings" name="buildings" className="my-0">
                <Switch
                  size="small"
                  checked={props.rfAnalysisForm.buildings === 1}
                  onChange={(value: any) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, buildings: value ? 1 : 0 })}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </Form.Item>
              <Form.Item label="Obstacles" name="obstacles" className="my-0">
                <Switch
                  size="small"
                  checked={props.rfAnalysisForm.obstacles === 1}
                  onChange={(value: any) => props.setRfAnalysisForm({ ...props.rfAnalysisForm, obstacles: value ? 1 : 0 })}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Form>
          </div>
        </Card>
      </div>

      <div
        className="position-absolute d-flex flex-column gap-2 overflow-auto"
        style={{ top: '16px', right: '16px', bottom: '16px', width: '384px' }}>
        <Card id="rf-basenodes-card" size="small" title={'Transmitters'}>
          <div className="d-flex flex-column gap-3 overflow-auto">
            {props.assets.map((asset: any) => (
              <Card key={asset.tracking_id} title={asset.tracking_id} size="small">
                {asset?.azimuth?.map((azimuth: any, azimuthIndex: number) => {
                  return (
                    <div key={asset.tracking_id} className="d-flex flex-row gap-1 ps-3">
                      <Typography.Text>{asset.tracking_id}</Typography.Text>
                      <Typography.Text>{':'}</Typography.Text>
                      <Typography.Text strong>{azimuthIndex + 1}</Typography.Text>
                    </div>
                  );
                })}
              </Card>
            ))}
          </div>
        </Card>
      </div>

      <Modal
        open={saveConfirmationVisible}
        title="Save Simulation"
        okText="Save"
        cancelText="Cancel"
        confirmLoading={isLoading}
        okButtonProps={{ disabled: simulationName.length === 0 }}
        onCancel={() => setSaveConfirmationVisible(false)}
        onOk={() => saveRfSimulation()}>
        <Typography.Text>Are you sure you want to save the simulation?</Typography.Text>
        <Divider />
        <Form autoComplete="off" labelAlign="left">
          <Form.Item label="Simulation Name" name="simulation_name" className="my-0">
            <Input value={simulationName} onChange={(e) => setSimulationName(e.target.value)} />
          </Form.Item>
        </Form>
        <Divider />
      </Modal>
    </>
  );
};

export default CreateRfSimulation;
